// userService.js
const API_BASE_URL = 'https://hosting-visioner7.com/api/v1_apibbva';

export const userService = {
    /**
     * Obtener datos del usuario actual
     */
    async getCurrentUser() {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/get_user.php`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });

            // Verificar si la respuesta es JSON válido
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (get_user):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success && data.usuario) {
                // Actualizar datos en localStorage
                localStorage.setItem('user', JSON.stringify(data.usuario));

                return {
                    success: true,
                    user: data.usuario
                };
            } else {
                // Si el token es inválido, cerrar sesión
                if (response.status === 401) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                }

                return {
                    success: false,
                    error: data.message || 'Error al obtener datos del usuario'
                };
            }
        } catch (error) {
            console.error('Error en getCurrentUser:', error);

            let errorMessage = 'Error de conexión con el servidor';
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    /**
     * Actualizar datos del usuario
     */
    async updateUser(userData, currentPassword = null) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Preparar datos para enviar
            const updateData = {};

            // Mapear campos del frontend al backend
            if (userData.firstName) updateData.nombre = userData.firstName;
            if (userData.lastName) updateData.apellido = userData.lastName;
            if (userData.email) updateData.email = userData.email;
            if (userData.phone !== undefined) updateData.telefono = userData.phone;
            if (userData.company) updateData.nombre_empresa = userData.company;
            if (userData.position) updateData.posicion = userData.position; // Nuevo campo
            if (userData.logoUrl) updateData.logo_empresa = userData.logoUrl;

            // Si hay contraseña nueva, incluirla
            if (userData.newPassword) {
                // Si se requiere contraseña actual para cambiar
                if (currentPassword && userData.currentPassword) {
                    // Verificar contraseña actual antes de cambiar
                    // Podrías agregar una verificación adicional aquí
                }
                updateData.password = userData.newPassword;
            }

            // Verificar que haya al menos un campo para actualizar
            if (Object.keys(updateData).length === 0) {
                return {
                    success: false,
                    error: 'No hay datos para actualizar'
                };
            }

            const response = await fetch(`${API_BASE_URL}/update_user.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(updateData)
            });

            // Verificar si la respuesta es JSON válido
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (update_user):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                // Si se actualizó el usuario, actualizar localStorage
                if (data.usuario) {
                    localStorage.setItem('user', JSON.stringify(data.usuario));
                } else {
                    // Si no viene en la respuesta, obtener datos actualizados
                    const userResult = await this.getCurrentUser();
                    if (userResult.success) {
                        data.user = userResult.user;
                    }
                }

                return {
                    success: true,
                    message: data.message || 'Datos actualizados correctamente',
                    user: data.usuario || data.user
                };
            } else {
                // Si el token es inválido, cerrar sesión
                if (response.status === 401) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                }

                return {
                    success: false,
                    error: data.message || 'Error al actualizar datos'
                };
            }
        } catch (error) {
            console.error('Error en updateUser:', error);

            let errorMessage = 'Error de conexión con el servidor';
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    /**
     * Cambiar solo la contraseña
     */
    async changePassword(currentPassword, newPassword) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/update_user.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    current_password: currentPassword,
                    password: newPassword
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (changePassword):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Contraseña actualizada correctamente'
                };
            } else {
                if (response.status === 401) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                }

                return {
                    success: false,
                    error: data.message || 'Error al cambiar contraseña'
                };
            }
        } catch (error) {
            console.error('Error en changePassword:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },


    /**
     * Subir logo de empresa
     */
    async uploadLogo(file) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Crear FormData
            const formData = new FormData();
            formData.append('logo', file);

            // Validar tipo de archivo
            if (!file.type.startsWith('image/')) {
                return {
                    success: false,
                    error: 'El archivo debe ser una imagen'
                };
            }

            // Validar tamaño (2MB)
            if (file.size > 2 * 1024 * 1024) {
                return {
                    success: false,
                    error: 'El archivo debe ser menor a 2MB'
                };
            }

            const response = await fetch(`${API_BASE_URL}/upload_logo.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // No establecer Content-Type cuando se usa FormData
                    // El navegador lo hará automáticamente con el boundary correcto
                },
                body: formData
            });

            // Verificar si la respuesta es JSON válido
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (uploadLogo):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                // Actualizar localStorage con el nuevo logo
                if (data.usuario) {
                    localStorage.setItem('user', JSON.stringify(data.usuario));
                }

                return {
                    success: true,
                    message: data.message || 'Logo subido correctamente',
                    url: data.url,
                    user: data.usuario
                };
            } else {
                if (response.status === 401) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                }

                return {
                    success: false,
                    error: data.message || 'Error al subir logo'
                };
            }
        } catch (error) {
            console.error('Error en uploadLogo:', error);

            let errorMessage = 'Error de conexión con el servidor';
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    /**
     * Obtener sesiones activas
     */
    async getActiveSessions() {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/sessions.php`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (sessions):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    sessions: data.sessions
                };
            } else {
                if (response.status === 401) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                }

                return {
                    success: false,
                    error: data.message || 'Error al obtener sesiones'
                };
            }
        } catch (error) {
            console.error('Error en getActiveSessions:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },




















    /**
 * Obtener todos los usuarios (solo para administradores)
 * @param {Object} filters - Filtros opcionales
 * @param {string} filters.search - Término de búsqueda
 * @param {string} filters.role - Rol específico
 * @param {number} filters.page - Página actual
 * @param {number} filters.limit - Elementos por página
 */
    async getAllUsers(filters = {}) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Construir query string con filtros
            const queryParams = new URLSearchParams();

            if (filters.search) queryParams.append('search', filters.search);
            if (filters.role) queryParams.append('rol', filters.role);
            if (filters.page) queryParams.append('page', filters.page.toString());
            if (filters.limit) queryParams.append('limit', filters.limit.toString());

            const queryString = queryParams.toString();
            const url = `${API_BASE_URL}/get_all_users.php${queryString ? `?${queryString}` : ''}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });

            // Verificar si la respuesta es JSON válido
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (getAllUsers):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                // Transformar datos para que coincidan con el formato del componente
                const formattedUsers = data.usuarios.map(user => ({
                    id: user.id,
                    name: `${user.nombre} ${user.apellido}`,
                    email: user.email,
                    phone: user.telefono || 'Sin teléfono',
                    role: this.mapRole(user.rol),
                    status: 'active', // Suponiendo que todos los usuarios en la respuesta están activos
                    joinDate: this.formatDate(user.fecha_creacion),
                    lastLogin: this.formatDate(user.fecha_actualizacion),
                    rawData: user // Mantener datos originales por si acaso
                }));

                return {
                    success: true,
                    users: formattedUsers,
                    rawUsers: data.usuarios, // Datos originales
                    pagination: data.pagination,
                    filters: data.filters || {},
                    estadisticas: data.estadisticas || null
                };
            } else {
                // Si el token es inválido o no tiene permisos
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || (response.status === 403 ?
                            'No tienes permisos de administrador' :
                            'Sesión inválida o expirada'),
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al obtener usuarios'
                };
            }
        } catch (error) {
            console.error('Error en getAllUsers:', error);

            let errorMessage = 'Error de conexión con el servidor';
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },

    /**
     * Mapear rol del backend al formato del frontend
     */
    mapRole(backendRole) {
        const roleMap = {
            'admin': 'Administrador',
            'usuario': 'Usuario Básico',
            'editor': 'Usuario Premium',
            'user': 'Usuario Básico'
        };

        return roleMap[backendRole] || backendRole;
    },

    /**
     * Formatear fecha para mostrar
     */
    formatDate(dateString) {
        if (!dateString) return 'No disponible';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    },

    /**
     * Crear nuevo usuario (solo para administradores)
     */
    async createUser(userData) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Validar datos requeridos
            if (!userData.email || !userData.password || !userData.nombre || !userData.apellido) {
                return {
                    success: false,
                    error: 'Faltan datos requeridos'
                };
            }

            const response = await fetch(`${API_BASE_URL}/register.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(userData)
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (createUser):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Usuario creado exitosamente',
                    userId: data.userId
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al crear usuario'
                };
            }
        } catch (error) {
            console.error('Error en createUser:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
        * Actualizar cualquier usuario como administrador
        */
    async updateUserAsAdmin(userId, userData) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Validar que tenemos al menos un campo para actualizar
            if (Object.keys(userData).length === 0) {
                return {
                    success: false,
                    error: 'No hay datos para actualizar'
                };
            }

            // Depuración: Mostrar datos que se enviarán
            console.log('Enviando datos a update_user_admin.php:', {
                usuario_id: userId,
                ...userData
            });

            const response = await fetch(`${API_BASE_URL}/update_user_admin.php`, {
                method: 'PUT', // O usa 'POST' si prefieres
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId,
                    ...userData
                })
            });

            // Depuración: Mostrar respuesta
            console.log('Respuesta HTTP status:', response.status);

            // Verificar si la respuesta es JSON válido
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (updateUserAsAdmin):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido',
                    responseText: text,
                    status: response.status
                };
            }

            const data = await response.json();
            console.log('Datos de respuesta:', data);

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Usuario actualizado exitosamente',
                    user: data.usuario
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al actualizar usuario',
                    statusCode: response.status
                };
            }
        } catch (error) {
            console.error('Error completo en updateUserAsAdmin:', error);

            let errorMessage = 'Error de conexión con el servidor';
            let errorDetails = error.message;

            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor. Verifica:';
                errorDetails = `
                1. Que el servidor esté en línea
                2. Que la URL sea correcta: ${API_BASE_URL}/update_user_admin.php
                3. Que no haya problemas de CORS
                4. Que tu conexión a internet funcione`;
            }

            return {
                success: false,
                error: errorMessage,
                details: errorDetails,
                fullError: error.toString()
            };
        }
    },

    /**
     * Cambiar rol de usuario (solo para administradores)
     */
    async changeUserRole(userId, newRole) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/update_user_role.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId,
                    nuevo_rol: newRole
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (changeUserRole):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Rol actualizado exitosamente'
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al actualizar rol'
                };
            }
        } catch (error) {
            console.error('Error en changeUserRole:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Desactivar/Activar usuario (solo para administradores)
     */
    async toggleUserStatus(userId, active) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/toggle_user_status.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId,
                    activo: active ? 1 : 0
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (toggleUserStatus):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Estado del usuario actualizado'
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al cambiar estado'
                };
            }
        } catch (error) {
            console.error('Error en toggleUserStatus:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Obtener estadísticas de usuarios
     */
    async getUserStats() {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Podemos obtener las estadísticas del endpoint de usuarios
            const usersResult = await this.getAllUsers({ limit: 1 });

            if (usersResult.success && usersResult.estadisticas) {
                return {
                    success: true,
                    stats: usersResult.estadisticas
                };
            } else {
                // Si no vienen en la respuesta, calcularlas manualmente
                const allUsers = await this.getAllUsers();

                if (allUsers.success) {
                    const stats = {
                        total_usuarios: allUsers.pagination?.total || allUsers.users?.length || 0,
                        total_admins: allUsers.rawUsers?.filter(u => u.rol === 'admin').length || 0,
                        total_usuarios_reg: allUsers.rawUsers?.filter(u => u.rol === 'usuario').length || 0,
                        total_editores: allUsers.rawUsers?.filter(u => u.rol === 'editor').length || 0,
                        nuevos_hoy: allUsers.rawUsers?.filter(u => {
                            const fecha = new Date(u.fecha_creacion);
                            const hoy = new Date();
                            return fecha.toDateString() === hoy.toDateString();
                        }).length || 0
                    };

                    return {
                        success: true,
                        stats
                    };
                }

                return allUsers;
            }
        } catch (error) {
            console.error('Error en getUserStats:', error);
            return {
                success: false,
                error: 'Error al obtener estadísticas'
            };
        }
    },

    /**
     * Verificar si el usuario actual es administrador
     */
    async checkAdminStatus() {
        try {
            const currentUser = await this.getCurrentUser();

            if (currentUser.success && currentUser.user) {
                // Verificar si es admin basado en el rol
                const isAdmin = currentUser.user.rol === 'admin' || currentUser.user.role === 'Administrador';

                return {
                    success: true,
                    isAdmin,
                    user: currentUser.user
                };
            }

            return currentUser;
        } catch (error) {
            console.error('Error en checkAdminStatus:', error);
            return {
                success: false,
                error: 'Error al verificar permisos'
            };
        }
    },

    /**
     * Obtener roles disponibles
     */
    getAvailableRoles() {
        return [
            { id: 'admin', name: 'Administrador' },
            { id: 'editor', name: 'Editor' },
            { id: 'usuario', name: 'Usuario' }
        ];
    },
























    // userService.js (agregar estas funciones al final)

    /**
     * Obtener datos de un usuario específico (para administradores)
     */
    async getUserById(userId) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/get_user_by_id.php?user_id=${userId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                },
                mode: 'cors'
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (getUserById):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    user: data.usuario
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al obtener usuario'
                };
            }
        } catch (error) {
            console.error('Error en getUserById:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Actualizar cualquier usuario como administrador
     */
    async updateUserAsAdmin(userId, userData) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Validar que tenemos al menos un campo para actualizar
            if (Object.keys(userData).length === 0) {
                return {
                    success: false,
                    error: 'No hay datos para actualizar'
                };
            }

            const response = await fetch(`${API_BASE_URL}/update_user_admin.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId,
                    ...userData
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (updateUserAsAdmin):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Usuario actualizado exitosamente',
                    user: data.usuario
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al actualizar usuario'
                };
            }
        } catch (error) {
            console.error('Error en updateUserAsAdmin:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Crear nuevo usuario como administrador
     */
    async createUserAsAdmin(userData) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Validar datos requeridos
            const requiredFields = ['email', 'password', 'nombre', 'apellido'];
            const missingFields = requiredFields.filter(field => !userData[field]);

            if (missingFields.length > 0) {
                return {
                    success: false,
                    error: `Faltan campos requeridos: ${missingFields.join(', ')}`
                };
            }

            const response = await fetch(`${API_BASE_URL}/create_user.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(userData)
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (createUserAsAdmin):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Usuario creado exitosamente',
                    userId: data.userId,
                    user: data.usuario
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al crear usuario'
                };
            }
        } catch (error) {
            console.error('Error en createUserAsAdmin:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Restablecer contraseña de usuario (para administradores)
     */
    async resetUserPassword(userId, newPassword) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/reset_user_password.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId,
                    nueva_password: newPassword
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (resetUserPassword):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Contraseña restablecida exitosamente'
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al restablecer contraseña'
                };
            }
        } catch (error) {
            console.error('Error en resetUserPassword:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    /**
     * Eliminar usuario permanentemente (para administradores)
     */
    async deleteUserPermanently(userId) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            const response = await fetch(`${API_BASE_URL}/delete_user.php`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify({
                    usuario_id: userId
                })
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (deleteUserPermanently):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Usuario eliminado exitosamente'
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al eliminar usuario'
                };
            }
        } catch (error) {
            console.error('Error en deleteUserPermanently:', error);
            return {
                success: false,
                error: 'Error de conexión con el servidor'
            };
        }
    },

    // Agrega esta función al userService.js

    /**
     * Subir logo/foto de perfil para cualquier usuario (solo administradores)
     */
    async uploadLogoForUser(userId, file) {
        try {
            const token = localStorage.getItem('auth_token');

            if (!token) {
                return {
                    success: false,
                    error: 'No hay sesión activa'
                };
            }

            // Crear FormData
            const formData = new FormData();
            formData.append('logo', file);
            formData.append('user_id', userId.toString());

            // Validar tipo de archivo
            if (!file.type.startsWith('image/')) {
                return {
                    success: false,
                    error: 'El archivo debe ser una imagen'
                };
            }

            // Validar tamaño (2MB)
            if (file.size > 2 * 1024 * 1024) {
                return {
                    success: false,
                    error: 'El archivo debe ser menor a 2MB'
                };
            }

            const response = await fetch(`${API_BASE_URL}/upload_logo_admin.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Respuesta no JSON (uploadLogoForUser):', text);
                return {
                    success: false,
                    error: 'El servidor respondió con un formato inválido'
                };
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    message: data.message || 'Foto de perfil actualizada correctamente',
                    url: data.url,
                    user: data.usuario
                };
            } else {
                if (response.status === 401 || response.status === 403) {
                    if (response.status === 401) {
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user');
                    }
                    return {
                        success: false,
                        error: data.message || 'No tienes permisos para esta acción',
                        statusCode: response.status
                    };
                }

                return {
                    success: false,
                    error: data.message || 'Error al subir foto de perfil'
                };
            }
        } catch (error) {
            console.error('Error en uploadLogoForUser:', error);

            let errorMessage = 'Error de conexión con el servidor';
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                errorMessage = 'No se pudo conectar con el servidor';
            }

            return {
                success: false,
                error: errorMessage
            };
        }
    },






};